import { useState, useEffect, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import domtoimage from "dom-to-image-more";
import certificateSmall from "../assets/images/cftn.PNG";
import certificateLarge from "../assets/images/cftp.PNG";
import generalBanner from "../assets/images/generalBanner.png";
import {
  FaTrash,
  FaCheckCircle,
  FaFileDownload,
  FaCalendarAlt,
} from "react-icons/fa";

export default function Donation() {
  const [selectedTab, setSelectedTab] = useState("Bkash");
  const [donationData, setDonationData] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  // Separate hidden rendering references to bypass React state delays
  const certificateRef = useRef(null);
  const generalBannerRef = useRef(null);

  // Hidden preview elements data state
  const [previewData, setPreviewData] = useState({
    name: "",
    amount: 0,
    memberId: "",
  });

  useEffect(() => {
    fetch("https://mosque-backend-yfae.vercel.app/allApproveDonation")
      .then((res) => res.json())
      .then((data) => setDonationData(data))
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  const filteredData = donationData.filter(
    (item) =>
      item.paymentMethod === selectedTab &&
      (!selectedDate || item.date === selectedDate),
  );

  const generateMemberId = (amount) => {
    const prefix = Number(amount) >= 100000 ? "PM" : "GM";
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `${prefix}${random}`;
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this donation?",
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `https://mosque-backend-yfae.vercel.app/deleteDonation/${id}`,
        {
          method: "DELETE",
        },
      );
      const result = await response.json();

      if (response.ok) {
        setDonationData((prev) => prev.filter((item) => item._id !== id));
        alert("Donation deleted successfully.");
      } else {
        alert(result.message || "Failed to delete donation.");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong.");
    }
  };

  const sendEmailWithPDF = async (donation) => {
    setLoadingId(donation._id);
    const generatedId = generateMemberId(donation.amount);
    const isPremium = Number(donation.amount) >= 100000;

    // 1. Force immediate sync update to the hidden DOM templates
    setPreviewData({
      name: donation.name,
      amount: Number(donation.amount),
      memberId: generatedId,
    });

    // 2. Small pause allowing browser layout thread to render template updates
    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const options = { bgcolor: "#ffffff", quality: 0.95 };

      // Capture the certificate image
      const certificateUrl = await domtoimage.toPng(
        certificateRef.current,
        options,
      );

      // Generate the uniform banner canvas layout asset for both tiers
      const bannerUrl = await domtoimage.toPng(
        generalBannerRef.current,
        options,
      );

      // Create PDF
      const pdf = new jsPDF("landscape", "pt", [800, 600]);

      // Page 1: Certificate
      pdf.addImage(certificateUrl, "PNG", 0, 0, 800, 600);

      // Page 2: Banner Asset Layout
      pdf.addPage([800, 533]);
      pdf.addImage(bannerUrl, "PNG", 0, 0, 800, 533);

      const pdfBlob = pdf.output("blob");
      const formData = new FormData();
      formData.append("pdf", pdfBlob, `${donation.name}-certificate.pdf`);
      formData.append("email", donation.email);
      formData.append("name", donation.name);
      formData.append("phone", donation.phone);
      formData.append("id", donation.transactionId);

      const memberData = {
        name: donation.name,
        email: donation.email,
        phone: donation.phone,
        category: isPremium ? "Premium" : "General",
        trxId: donation.transactionId,
        memberId: generatedId,
      };

      const response = await fetch(
        "https://mosque-backend-yfae.vercel.app/sendEmail",
        {
          method: "POST",
          body: formData,
        },
      );

      const result = await response.text();

      if (response.ok) {
        await fetch("https://mosque-backend-yfae.vercel.app/addMember", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(memberData),
        });

        await fetch("https://mosque-backend-yfae.vercel.app/donation", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(donation),
        });

        alert(`Email sent successfully to ${donation.email}`);
        setDonationData((prev) => prev.filter((d) => d._id !== donation._id));
      } else {
        alert(result);
      }
    } catch (error) {
      console.error("Error generating or transferring document:", error);
      alert("Error generating elements. Please check template sources.");
    } finally {
      setLoadingId(null);
    }
  };

  const downloadTableAsPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [["Date", "Name", "Email", "Phone", "Amount", "Transaction ID"]],
      body: filteredData.map((donation) => [
        donation.date,
        donation.name,
        donation.email,
        donation.phone,
        donation.amount,
        donation.transactionId,
      ]),
      styles: { fillColor: [254, 243, 199] },
      headStyles: { fillColor: [249, 115, 22] },
    });
    doc.save(`donations-${selectedTab}.pdf`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <h1 className="text-3xl font-extrabold text-center text-gray-800 tracking-tight">
          Donation Approvals Management
        </h1>
        <p className="text-center text-gray-500 mt-1">
          Filter, review entry verification status, and issue access cards
        </p>

        {/* Payment Method Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          {["Bkash", "Nogod", "Rocket", "Card", "Bank"].map((method) => (
            <button
              key={method}
              className={`px-5 py-2.5 font-semibold text-sm rounded-lg transition-all duration-200 shadow-sm ${
                selectedTab === method
                  ? "bg-orange-600 text-white scale-105 shadow-orange-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedTab(method)}
            >
              {method}
            </button>
          ))}
        </div>

        {/* Date Filters */}
        <div className="flex justify-center items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <div className="relative flex items-center">
            <FaCalendarAlt className="absolute left-3 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Donation Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700 uppercase tracking-wider text-xs font-bold">
              <tr>
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Personal Profile</th>
                <th className="p-4 text-left">Contact Info</th>
                <th className="p-4 text-center">Amount</th>
                <th className="p-4 text-left">Transaction ID</th>
                <th className="p-4 text-center">Receipt</th>
                <th className="p-4 text-center">Action</th>
                <th className="p-4 text-center">Remove</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredData.length > 0 ? (
                filteredData.map((donation) => (
                  <tr
                    key={donation._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-4 whitespace-nowrap text-gray-600">
                      {donation.date}
                    </td>
                    <td className="p-4 font-medium text-gray-900">
                      {donation.name}
                    </td>
                    <td className="p-4 text-gray-500">
                      <div>{donation.email}</div>
                      <div className="text-xs text-gray-400">
                        {donation.phone}
                      </div>
                    </td>
                    <td className="p-4 whitespace-nowrap text-center font-bold text-emerald-600">
                      ৳{Number(donation.amount).toLocaleString()}
                    </td>
                    <td className="p-4 font-mono text-xs text-gray-600 select-all">
                      {donation.transactionId}
                    </td>
                    <td className="p-4 text-center">
                      {donation.imageUrl ? (
                        <img
                          src={donation.imageUrl}
                          alt="Verification Proof"
                          className="w-10 h-10 rounded shadow-sm object-cover mx-auto cursor-zoom-in hover:scale-110 transition-transform"
                          onClick={() => setSelectedImage(donation.imageUrl)}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">No Image</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => sendEmailWithPDF(donation)}
                        className={`px-3 py-2 text-xs font-medium rounded-lg text-white flex items-center justify-center gap-1.5 mx-auto w-32 shadow-sm transition-all ${
                          loadingId === donation._id
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-emerald-600 hover:bg-emerald-700 active:scale-95"
                        }`}
                        disabled={loadingId !== null}
                      >
                        {loadingId === donation._id ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <FaCheckCircle />
                            Approve & Send
                          </>
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleDelete(donation._id)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors mx-auto flex items-center justify-center"
                        title="Delete record"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center py-12 text-gray-400 font-medium"
                  >
                    No matching donation records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Toolbar */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={downloadTableAsPDF}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-medium text-sm rounded-lg shadow-sm transition-all"
        >
          <FaFileDownload />
          Download Table Log (PDF)
        </button>
      </div>

      {/* Image Modal Preview */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-center items-center p-4 animate-fadeIn"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] bg-white p-2 rounded-xl shadow-2xl">
            <img
              src={selectedImage}
              alt="Enlarged Document"
              className="max-w-full max-h-[85vh] rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* HIDDEN BACKGROUND GRAPHIC DOM TEMPLATES                  */}
      {/* ======================================================== */}
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          overflow: "hidden",
        }}
      >
        {/* Certificate Section */}
        <div
          id="certificate-render-node"
          ref={certificateRef}
          style={{
            width: "800px",
            height: "600px",
            backgroundImage: `url(${previewData.amount >= 100000 ? certificateLarge : certificateSmall})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "transparent",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "36px",
              fontWeight: "bold",
              color: "#000",
              textAlign: "center",
              width: "500px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "'Great Vibes', serif",
            }}
          >
            {previewData.name}
          </div>
        </div>

        {/* Banner Layout Area (Shared by General and Premium) */}
        <div
          id="general-banner-render-node"
          ref={generalBannerRef}
          style={{
            width: "800px",
            height: "533px",
            backgroundImage: `url(${generalBanner})`,
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            position: "relative",
            fontFamily: "'Georgia', serif",
          }}
        >
          {/* Name */}
          <div
            style={{
              position: "absolute",
              top: "130px",
              left: "360px",
              width: "165px",
              fontSize: "10px",
              fontWeight: "700",
              color: "#C8A03A",
              textAlign: "center",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              border: "none",
              outline: "none",
              boxShadow: "none",
            }}
          >
            {previewData.name}
          </div>
          {/* Member ID */}
          <div
            style={{
              position: "absolute",
              top: "160px",
              left: "370px",
              width: "120px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#C8A03A",
              textAlign: "center",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              border: "none",
              outline: "none",
              boxShadow: "none",
            }}
          >
            {previewData.memberId}
          </div>
          {/* Tier type designation dynamically driven by tier amount layout details */}
          <div
            style={{
              position: "absolute",
              top: "193px",
              left: "370px",
              width: "120px",
              fontSize: "11px",
              fontWeight: "700",
              color: "#C8A03A",
              textAlign: "center",
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              border: "none",
              outline: "none",
              boxShadow: "none",
            }}
          >
            {previewData.amount >= 100000 ? "Premium" : "General"}
          </div>
        </div>
      </div>
    </div>
  );
}
